import { useEffect, useRef, useState } from 'react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { PhoneField } from '@/components/PhoneField';
import { FormField } from '@/components/FormField';
import { whatsappAccountService, ConnectAccountInput, WhatsAppConnectionConfig, WhatsappAccount } from '@/services/whatsapp-account.service';
import { loadFacebookSdk, launchEmbeddedSignup } from '@/lib/facebookSdk';
import { Info, Zap, KeyRound, AlertTriangle, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';


type TabKey = 'auto' | 'manual';

interface Props {
  open: boolean;
  reconnectAccount?: WhatsappAccount | null;
  onClose: () => void;
  onConnected: () => void;
}

const EMPTY: ConnectAccountInput = {
  businessName: '',
  wabaId: '',
  appId: '',
  phoneNumberId: '',
  displayPhoneNumber: '',
  verifiedName: '',
  accessToken: '',
  setAsDefault: false,
};

export function ConnectAccountModal({ open, onClose, onConnected, reconnectAccount }: Props) {
  const [form, setForm] = useState<ConnectAccountInput>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [esBusy, setEsBusy] = useState(false);
  const [tab, setTab] = useState<TabKey>('auto');
  const [config, setConfig] = useState<WhatsAppConnectionConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState('');
  const signupAbort = useRef<AbortController | null>(null);
  const [registerNew, setRegisterNew] = useState(false);
  const [registrationPin, setRegistrationPin] = useState('');
  const configId = config?.configId ?? '';
  const signupAvailable = !!config?.available;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setForm(reconnectAccount ? { ...EMPTY, businessName: reconnectAccount.businessName, wabaId: reconnectAccount.wabaId,
      appId: reconnectAccount.appId, phoneNumberId: reconnectAccount.phoneNumberId, displayPhoneNumber: reconnectAccount.displayPhoneNumber,
      verifiedName: reconnectAccount.verifiedName, reconnectAccountUid: reconnectAccount.accountUid } : EMPTY);
    setRegisterNew(false); setRegistrationPin(''); setConfig(null); setSdkReady(false);
    setConfigLoading(true);
    whatsappAccountService.connectionConfig().then(value => { if (!cancelled) setConfig(value); })
      .catch(() => { if (!cancelled) setConfig(null); })
      .finally(() => { if (!cancelled) setConfigLoading(false); });
    return () => { cancelled = true; signupAbort.current?.abort(); };
  }, [open, reconnectAccount]);

  useEffect(() => {
    if (!open || !signupAvailable || !config?.appId) return;
    let cancelled = false;
    setSdkReady(false);
    setSdkError('');
    loadFacebookSdk(config.appId, config.graphVersion).then(() => { if (!cancelled) setSdkReady(true); })
      .catch(error => { if (!cancelled) setSdkError(error.message); });
    return () => { cancelled = true; };
  }, [open, signupAvailable, config?.appId, config?.graphVersion]);

  const set = <K extends keyof ConnectAccountInput>(key: K, value: ConnectAccountInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const reset = () => { setForm(EMPTY); setRegistrationPin(''); setRegisterNew(false); };

  const onEmbeddedSignup = async () => {
    if (!sdkReady || !signupAvailable || (registerNew && registrationPin.length !== 6)) return;
    const controller = new AbortController();
    signupAbort.current = controller;
    setEsBusy(true);
    try {
      const result = await launchEmbeddedSignup(configId, controller.signal);
      if (controller.signal.aborted) return;
      setBusy(true);
      const acc = await whatsappAccountService.embeddedSignupAttach({
        code: result.code,
        wabaId: result.wabaId,
        phoneNumberId: result.phoneNumberId,
        setAsDefault: reconnectAccount ? reconnectAccount.isDefault : true,
        reconnectAccountUid: reconnectAccount?.accountUid,
        registrationPin: registerNew ? registrationPin : undefined,
      });
      if (acc.webhookSubscriptionError || acc.registrationError) toast('Account saved. Open its settings to finish the connection.', { icon: '⚠️', duration: 7000 });
      else toast.success(`${acc.businessName} ${reconnectAccount ? 'reconnected' : 'connected'} via Facebook`);
      setRegistrationPin('');
      onConnected();
    } catch (e: any) {
      // The axios interceptor already toasts response errors with a server-supplied
      // message. Only surface a fallback for non-HTTP failures (e.g. Meta SDK fails
      // to load) where there's no response body for the interceptor to display.
      if (!controller.signal.aborted && !e?.response) toast.error(e?.message ?? 'Embedded Signup failed');
    } finally { setEsBusy(false); setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const acc = await whatsappAccountService.connect(form);
      if (acc.webhookSubscriptionError) toast('Account saved. Repair the webhook connection from its account card.', { icon: '⚠️', duration: 7000 });
      else toast.success(`${form.businessName} ${reconnectAccount ? 'reconnected' : 'connected'}`);
      reset();
      onConnected();
    } catch (e: any) {
      // Axios interceptor already toasts the server message. Only fallback for
      // non-HTTP errors (network / programming bug).
      if (!e?.response) toast.error(e?.message ?? 'Failed to connect account');
    } finally { setBusy(false); }
  };

  const handleClose = () => { if (!busy) { signupAbort.current?.abort(); reset(); onClose(); } };

  const requiredFilled =
    form.businessName.trim() &&
    form.wabaId.trim() &&
    form.appId.trim() &&
    form.phoneNumberId.trim() &&
    form.displayPhoneNumber.trim() &&
    form.verifiedName.trim() &&
    form.accessToken.trim();

  const TabButton: React.FC<{ k: TabKey; icon: React.ReactNode; label: string; sub: string }> = ({ k, icon, label, sub }) => (
    <button
      type="button"
      onClick={() => setTab(k)}
      disabled={busy || esBusy}
      className={clsx(
        'flex flex-1 items-start gap-3 rounded-xl border px-4 py-3 text-left transition-all',
        tab === k
          ? 'border-primary bg-primary-soft shadow-soft'
          : 'border-border bg-surface hover:bg-surface-alt',
      )}
    >
      <div className={clsx(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
        tab === k ? 'bg-gold-gradient text-on-primary' : 'bg-surface-alt text-text-muted',
      )}>{icon}</div>
      <div className="min-w-0">
        <div className={clsx('text-sm font-semibold', tab === k ? 'text-primary' : 'text-text-primary')}>{label}</div>
        <div className="text-2xs text-text-muted">{sub}</div>
      </div>
    </button>
  );

  return (
    <Modal open={open} onClose={handleClose} title={reconnectAccount ? `Reconnect ${reconnectAccount.businessName}` : "Connect WhatsApp Account"} widthClass="max-w-2xl">
      <div className="px-6 py-5 space-y-4">
        {reconnectAccount && <p className="rounded-lg border border-border bg-surface-alt p-3 text-xs text-text-secondary">Renew authorization for <strong>{reconnectAccount.displayPhoneNumber}</strong>. Select this same business and number in Facebook. Chats, templates and account settings stay linked.</p>}
        {/* Tabs */}
        <div className="flex gap-3">
          <TabButton
            k="auto"
            icon={<Zap size={16} />}
            label="Connect with Facebook"
            sub="Choose your business and verify your number"
          />
          <TabButton
            k="manual"
            icon={<KeyRound size={16} />}
            label="Manual"
            sub="Paste WABA, phone-number ID, and access token"
          />
        </div>

        {/* TAB 1 — Auto / Embedded Signup */}
        {tab === 'auto' && (
          <div className="rounded-xl border border-border bg-surface-alt/40 p-4">
            {configLoading ? <p className="text-sm text-text-muted">Checking connection options…</p> : signupAvailable ? (
              <>
                <p className="mb-3 text-xs text-text-secondary">
                  We'll open Meta's secure WhatsApp signup popup. There you can pick (or create) a WhatsApp Business
                  Account, enter your business phone number, verify it via SMS or voice, and set your display name.
                  When you finish, we automatically wire it into SlightorCRM — no IDs or tokens to copy.
                </p>
                {!reconnectAccount && <div className="mb-4 space-y-2 rounded-lg border border-border p-3">
                  <label className="flex items-center gap-2 text-xs text-text-primary"><input type="checkbox" checked={registerNew} onChange={e => setRegisterNew(e.target.checked)} className="accent-primary" />Register a new Cloud API number after signup</label>
                  {registerNew && <><Input aria-label="Registration PIN" type="password" inputMode="numeric" autoComplete="new-password" maxLength={6} placeholder="Choose a six-digit PIN" value={registrationPin} onChange={e => setRegistrationPin(e.target.value.replace(/\D/g, ''))} /><p className="text-2xs text-text-muted">Keep this two-step verification PIN safe. Use the existing PIN if the number already has one. It is sent to Meta and is not stored in CRM.</p></>}
                </div>}
                {sdkError && <div className="mb-3 space-y-2 text-xs text-warn"><p>{sdkError}</p><Button variant="secondary" onClick={() => {
                  setSdkError('');
                  loadFacebookSdk(config!.appId, config!.graphVersion).then(() => setSdkReady(true)).catch(error => setSdkError(error.message));
                }}>Retry Facebook connection</Button></div>}
                <Button
                  variant="primary"
                  loading={esBusy}
                  disabled={esBusy || busy || !sdkReady || (registerNew && registrationPin.length !== 6)}
                  onClick={onEmbeddedSignup}
                  className="w-full"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                    <path d="M22 12.07C22 6.48 17.52 2 11.93 2S2 6.48 2 12.07c0 4.99 3.66 9.13 8.44 9.93v-7.02H7.9v-2.91h2.54V9.84c0-2.51 1.49-3.89 3.78-3.89 1.09 0 2.24.19 2.24.19v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.91h-2.34V22c4.78-.8 8.43-4.94 8.43-9.93z"/>
                  </svg>
                  Continue with Facebook
                </Button>
              </>
            ) : (
              <div className="flex items-start gap-3">
                <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warn" />
                <div className="space-y-2 text-xs text-text-secondary">
                  <p className="font-semibold text-text-primary">Facebook connection is not available yet.</p>
                  <p>Your platform administrator needs to finish the Facebook connection setup. You can connect an existing WhatsApp Business account using the Manual tab.</p>
                  <Button variant="secondary" iconLeft={<RefreshCw size={13} />} onClick={() => {
                    setConfigLoading(true);
                    whatsappAccountService.connectionConfig().then(setConfig).catch(() => setConfig(null)).finally(() => setConfigLoading(false));
                  }}>Check again</Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* TAB 2 — Manual */}
        {tab === 'manual' && (
          <>
            <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info-soft px-3 py-2.5 mb-4">
              <Info size={14} className="mt-0.5 shrink-0 text-info" />
              <p className="text-xs text-text-secondary">
                Find these values in <strong>Meta Business Manager → WhatsApp → API Setup</strong>. Your access token is encrypted before storage.
              </p>
            </div>

        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <FormField label="Business name" required>
            <Input
              placeholder="e.g. Acme Inc"
              value={form.businessName}
              onChange={(e) => set('businessName', e.target.value)}
            />
          </FormField>
          <FormField label="Verified name" required hint="As shown in Meta">
            <Input
              placeholder="e.g. Acme Inc"
              value={form.verifiedName}
              onChange={(e) => set('verifiedName', e.target.value)}
            />
          </FormField>

          <FormField label="WhatsApp Business Account ID (WABA ID)" required>
            <Input
              placeholder="15-digit numeric ID"
              disabled={!!reconnectAccount}
              value={form.wabaId}
              onChange={(e) => set('wabaId', e.target.value.replace(/\D/g, ''))}
            />
          </FormField>
          <FormField label="App ID" required>
            <Input
              placeholder="15-digit numeric ID"
              value={form.appId}
              onChange={(e) => set('appId', e.target.value.replace(/\D/g, ''))}
            />
          </FormField>

          <FormField label="Phone Number ID" required>
            <Input
              placeholder="15-digit numeric ID"
              disabled={!!reconnectAccount}
              value={form.phoneNumberId}
              onChange={(e) => set('phoneNumberId', e.target.value.replace(/\D/g, ''))}
            />
          </FormField>
          <FormField label="Display phone number" required>
            <PhoneField
              placeholder="Enter business phone number"
              disabled={!!reconnectAccount}
              value={form.displayPhoneNumber}
              onChange={(e164) => set('displayPhoneNumber', e164)}
            />
          </FormField>
        </div>

        <FormField label="System User access token" required hint="Long-lived token from Business Manager">
          <Input
            type="password"
            placeholder="EAA..."
            value={form.accessToken}
            onChange={(e) => set('accessToken', e.target.value)}
          />
        </FormField>

        <FormField label="App secret" hint="Optional — used for webhook signature verification">
          <Input
            type="password"
            placeholder="leave blank to inherit from server config"
            value={form.appSecret ?? ''}
            onChange={(e) => set('appSecret', e.target.value)}
          />
        </FormField>

        <label className="mt-2 flex items-center gap-2 text-sm text-text-primary cursor-pointer">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-border text-primary focus:ring-primary-soft"
            checked={!!form.setAsDefault}
            onChange={(e) => set('setAsDefault', e.target.checked)}
          />
          <span>Set as default account</span>
        </label>
          </>
        )}
      </div>

      {tab === 'manual' ? (
        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="secondary" onClick={handleClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={submit} loading={busy} disabled={!requiredFilled || busy}>
            {reconnectAccount ? 'Renew authorization' : 'Connect Account'}
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="secondary" onClick={handleClose} disabled={busy}>Cancel</Button>
        </div>
      )}
    </Modal>
  );
}
